import React, { useEffect, useState } from 'react';
import { Box, Grid, useTheme, useMediaQuery, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, LinearProgress, Button, Chip } from '@mui/material';
import { ActivityIcon, BarChart3Icon, LayoutDashboardIcon, ClockIcon, RefreshCcwIcon, CheckCircle2Icon, DollarSignIcon, BanknoteIcon, HourglassIcon, WalletIcon, ArrowRightLeftIcon, SendIcon, CalendarClockIcon, TimerIcon, RotateCcwIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import ChartCard from '../components/ChartCard';
import KPICard from '../components/KPICard';
import { VALID_MTOS } from '../utils/constants';
interface DashboardProps {
  onLogout: () => void;
}
// Mock Data Generators
const PKR_USD_RATE = 280;
const generateRealtimeTableData = (selectedPartner: string) => {
  const partners = selectedPartner === 'all' ? VALID_MTOS : [selectedPartner];
  return partners.map((partner, i) => {
    const todayAmount = 45000 + i * 15000;
    return {
      partner,
      todayTotal: 150 + i * 45,
      todaySuccess: 142 + i * 40,
      todayAmount,
      todayAmountUSD: Math.round(todayAmount / PKR_USD_RATE),
      mtdTotal: 3500 + i * 1200,
      mtdSuccessPct: 94 + i % 5,
      ytdTotal: 45000 + i * 15000,
      ytdSuccessPct: 95 + i % 4
    };
  });
};
const generateSummaryTableData = (selectedPartner: string, selectedTimeRange: string) => {
  const partners = selectedPartner === 'all' ? VALID_MTOS : [selectedPartner];
  const multiplier = selectedTimeRange === 'ytd' ? 12 : 1;
  return partners.map((partner, i) => {
    const total = (5000 + i * 2000) * multiplier;
    const success = Math.floor(total * 0.92);
    const inProgress = Math.floor(total * 0.05);
    const failed = total - success - inProgress;
    const totalAmount = total * 300;
    const successAmount = success * 300;
    return {
      partner,
      total,
      success,
      successPct: (success / total * 100).toFixed(1),
      inProgress,
      failed,
      totalAmount,
      successAmount,
      totalAmountUSD: Math.round(totalAmount / PKR_USD_RATE),
      successAmountUSD: Math.round(successAmount / PKR_USD_RATE)
    };
  });
};
const Dashboard: React.FC<DashboardProps> = ({
  onLogout
}) => {
  const bankName = localStorage.getItem('bankName') || 'OpenRemit Bank';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [view, setView] = useState<'realtime' | 'summary'>('realtime');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [timeRange, setTimeRange] = useState<'today' | 'mtd' | 'ytd'>('today');
  const currency = 'PKR';
  const [partner, setPartner] = useState('all');
  const [corridor, setCorridor] = useState('all');
  const [perfPage, setPerfPage] = useState(0);
  const [perfRowsPerPage, setPerfRowsPerPage] = useState(10);
  const [rtPage, setRtPage] = useState(0);
  const [rtRowsPerPage, setRtRowsPerPage] = useState(10);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.toISOString().slice(0, 16);
  });
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: 'realtime' | 'summary' | null) => {
    if (newView !== null) {
      setView(newView);
      // Real-time always uses "today"; Performance defaults to "mtd"
      if (newView === 'realtime') {
        setTimeRange('today');
      } else if (timeRange === 'today') {
        setTimeRange('mtd');
      }
      refreshData();
    }
  };
  const handleTimeRangeChange = (_: React.MouseEvent<HTMLElement>, newRange: 'today' | 'mtd' | 'ytd' | null) => {
    if (newRange !== null) {
      setTimeRange(newRange);
      refreshData();
    }
  };
  const refreshData = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastRefresh(new Date());
      setIsRefreshing(false);
    }, 800);
  };
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (view === 'realtime') {
      interval = setInterval(() => {
        refreshData();
      }, 5 * 60 * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [view]);
  const realtimeData = generateRealtimeTableData(partner);
  const summaryData = generateSummaryTableData(partner, timeRange);
  // ===== DYNAMIC KPI STATS based on view, timeRange, partner, currency =====
  //
  // TREND COMPARISON LABELS (applied to all 7 KPI cards below):
  //   Real-time (today)     → "vs previous 24 hours"
  //   Performance MTD       → "vs previous 30 days"
  //   Performance YTD       → "vs previous 365 days"
  //
  // Applied in:
  //   1. Remittance Received   — Remittance Statistics section, KPI row
  //   2. Remittance Processed  — Remittance Statistics section, KPI row
  //   3. Remittance Pending    — Remittance Statistics section, KPI row
  //   4. Remittance Reversed   — Remittance Statistics section, KPI row
  //   5. Processed via Cash    — Payout Breakdown section
  //   6. Processed via FT      — Payout Breakdown section
  //   7. Processed via IBFT    — Payout Breakdown section
  //
  // Formula: received = processed (cash + ft + ibft) + pending + failed + reversed
  //
  const computeKPIStats = () => {
    const timeMultiplier = timeRange === 'today' ? 1 : timeRange === 'mtd' ? 22 : 260;
    const currencyFactor = 1;
    const now = new Date();
    const minutesAgo = Math.floor(Math.random() * 3) + 1;
    const lastTxnDate = new Date(now.getTime() - minutesAgo * 60000);
    const lastTxnFormatted = lastTxnDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const baseAvgTime = view === 'realtime' ? 0.3 : 0.5;
    const timeRangeAdjust = timeRange === 'today' ? 0 : timeRange === 'mtd' ? 0.1 : 0.2;
    const avgTxnTime = (baseAvgTime + timeRangeAdjust).toFixed(1);
    const avgTxnTrend = view === 'realtime' ? [3.2, 2.9, 2.7, 2.5, 2.3, 2.4, 2.2, 2.1, 2.3, 2.0, 2.1, 2.1] : [3.8, 3.5, 3.2, 3.0, 2.9, 3.1, 2.8, 2.7, 2.9, 2.8, 2.8, 2.8];
    if (view === 'realtime') {
      const totalReceived = realtimeData.reduce((acc, d) => acc + d.todayTotal, 0);
      const totalSuccess = realtimeData.reduce((acc, d) => acc + d.todaySuccess, 0);
      // Payout breakdown from processed (success)
      const cashPct = 0.36;
      const ftPct = 0.38;
      const ibftPct = 0.26;
      const cash = Math.round(totalSuccess * cashPct);
      const ft = Math.round(totalSuccess * ftPct);
      const ibft = Math.round(totalSuccess * ibftPct);
      const processed = cash + ft + ibft; // processed = sum of payout channels
      // Failed and reversed are small portions of received
      const failed = Math.round(totalReceived * 0.03);
      const reversed = Math.round(totalReceived * 0.02);
      const cancelled = Math.round(totalReceived * 0.015);
      const pending = totalReceived - processed - failed - reversed - cancelled;
      const received = processed + pending + failed + reversed + cancelled;
      const trendBase = timeRange === 'today' ? [5, 7, 8, 9, 11, 10, 12, 14, 15, 13, 14, 16] : timeRange === 'mtd' ? [80, 95, 110, 105, 120, 130, 125, 140, 150, 145, 155, 160] : [800, 950, 1100, 1050, 1200, 1300, 1250, 1400, 1500, 1450, 1550, 1600];
      // Compute percentages for pie chart
      const processedPct = Math.round(processed / received * 100);
      const failedPct = Math.round(failed / received * 100);
      const reversedPct = Math.round(reversed / received * 100);
      const cancelledPct = Math.round(cancelled / received * 100);
      const pendingPct = 100 - processedPct - failedPct - reversedPct - cancelledPct;
      return {
        received: {
          value: received.toLocaleString(),
          trend: 12.5,
          data: trendBase
        },
        processed: {
          value: processed.toLocaleString(),
          trend: 8.3,
          data: trendBase.map(v => Math.round(v * 0.92))
        },
        pending: {
          value: pending.toLocaleString(),
          trend: 15.2,
          data: trendBase.map(v => Math.round(v * 0.05))
        },
        reversed: {
          value: reversed.toLocaleString(),
          trend: -1.8,
          data: trendBase.map(v => Math.round(v * 0.02))
        },
        cash: {
          value: cash.toLocaleString(),
          trend: 9.7,
          data: trendBase.map(v => Math.round(v * cashPct))
        },
        ft: {
          value: ft.toLocaleString(),
          trend: 22.8,
          data: trendBase.map(v => Math.round(v * ftPct))
        },
        ibft: {
          value: ibft.toLocaleString(),
          trend: -5.2,
          data: trendBase.map(v => Math.round(v * ibftPct))
        },
        lastTxnDateTime: lastTxnFormatted,
        avgTxnTime: `${avgTxnTime}s`,
        avgTxnTrend: avgTxnTrend,
        pieValues: [processedPct, failedPct, pendingPct, reversedPct, cancelledPct],
        pieCounts: [processed, failed, pending, reversed, cancelled]
      };
    } else {
      // Performance view — scale base numbers by timeMultiplier (MTD ~22 days, YTD ~260 days)
      const baseTxns = summaryData.reduce((acc, d) => acc + d.total, 0);
      const baseSuccess = summaryData.reduce((acc, d) => acc + d.success, 0);
      const baseInProgress = summaryData.reduce((acc, d) => acc + d.inProgress, 0);
      const baseFailed = baseTxns - baseSuccess - baseInProgress;
      // Apply time multiplier so MTD and YTD show different magnitudes
      const scaledSuccess = Math.round(baseSuccess * timeMultiplier);
      const scaledInProgress = Math.round(baseInProgress * timeMultiplier);
      const scaledFailed = Math.round(baseFailed * timeMultiplier);
      // Payout breakdown from processed (cash + ft + ibft = processed)
      const cash = Math.round(scaledSuccess * 0.4);
      const ft = Math.round(scaledSuccess * 0.35);
      const ibft = Math.round(scaledSuccess * 0.25);
      const processed = cash + ft + ibft;
      const pending = scaledInProgress;
      const failed = scaledFailed;
      const reversed = Math.round(baseTxns * 0.02 * timeMultiplier);
      const cancelled = Math.round(baseTxns * 0.015 * timeMultiplier);
      // received = processed + pending + failed + reversed + cancelled
      const received = processed + pending + failed + reversed + cancelled;
      const perfTrend = [1200, 1900, 1500, 2200, 2800, 3100, 2900, 3400, 3200, 3600, 3800, 4100].map(v => Math.round(v * (timeMultiplier / 22))); // scale trend data too
      const processedPct = Math.round(processed / received * 100);
      const failedPct = Math.round(failed / received * 100);
      const reversedPct = Math.round(reversed / received * 100);
      const cancelledPct = Math.round(cancelled / received * 100);
      const pendingPct = 100 - processedPct - failedPct - reversedPct - cancelledPct;
      return {
        received: {
          value: received.toLocaleString(),
          trend: 6.8,
          data: perfTrend
        },
        processed: {
          value: processed.toLocaleString(),
          trend: 4.2,
          data: perfTrend.map(v => Math.round(v * 0.92))
        },
        pending: {
          value: pending.toLocaleString(),
          trend: -3.1,
          data: perfTrend.map(v => Math.round(v * 0.05))
        },
        reversed: {
          value: reversed.toLocaleString(),
          trend: -2.1,
          data: perfTrend.map(v => Math.round(v * 0.02))
        },
        cash: {
          value: cash.toLocaleString(),
          trend: 7.5,
          data: perfTrend.map(v => Math.round(v * 0.4))
        },
        ft: {
          value: ft.toLocaleString(),
          trend: 11.3,
          data: perfTrend.map(v => Math.round(v * 0.35))
        },
        ibft: {
          value: ibft.toLocaleString(),
          trend: -2.8,
          data: perfTrend.map(v => Math.round(v * 0.25))
        },
        lastTxnDateTime: lastTxnFormatted,
        avgTxnTime: `${avgTxnTime}s`,
        avgTxnTrend: avgTxnTrend,
        pieValues: [processedPct, failedPct, pendingPct, reversedPct, cancelledPct],
        pieCounts: [processed, failed, pending, reversed, cancelled]
      };
    }
  };
  const kpiStats = computeKPIStats();
  const pieChartData = {
    labels: ['Processed', 'Failed', 'Pending', 'Reversed', 'Cancelled'],
    values: kpiStats.pieValues,
    counts: kpiStats.pieCounts,
    colors: ['#10B981', '#EF4444', '#F59E0B', '#3B82F6', '#94A3B8']
  };
  const barChartData = {
    categories: realtimeData.map(d => d.partner.split(' ')[0]),
    series: [{
      name: 'Total Amount (PKR)',
      data: realtimeData.map(d => d.todayAmount),
      color: '#E2E8F0'
    }, {
      name: 'Success Amount (PKR)',
      data: realtimeData.map(d => d.todayAmount * 0.95),
      color: '#0EA5E9'
    }]
  };
  const CORRIDORS = [{
    code: 'PK-AE',
    label: 'UAE',
    flag: '🇦🇪'
  }, {
    code: 'PK-SA',
    label: 'Saudi Arabia',
    flag: '🇸🇦'
  }, {
    code: 'PK-UK',
    label: 'United Kingdom',
    flag: '🇬🇧'
  }, {
    code: 'PK-US',
    label: 'United States',
    flag: '🇺🇸'
  }, {
    code: 'PK-KW',
    label: 'Kuwait',
    flag: '🇰🇼'
  }, {
    code: 'PK-QA',
    label: 'Qatar',
    flag: '🇶🇦'
  }, {
    code: 'PK-BH',
    label: 'Bahrain',
    flag: '🇧🇭'
  }, {
    code: 'PK-OM',
    label: 'Oman',
    flag: '🇴🇲'
  }];
  const corridorTxnData = (() => {
    const multiplier = timeRange === 'today' ? 1 : timeRange === 'mtd' ? 22 : 260;
    const base = [320, 280, 190, 170, 95, 75, 55, 40];
    const data = CORRIDORS.map((c, i) => ({
      ...c,
      txns: Math.round(base[i] * multiplier * (corridor === 'all' || corridor === c.code ? 1 : 0)),
      amount: Math.round(base[i] * multiplier * 300 * (corridor === 'all' || corridor === c.code ? 1 : 0))
    })).filter(d => d.txns > 0);
    return data;
  })();
  const countryChartData = {
    labels: corridorTxnData.map(d => `${d.flag} ${d.label}`),
    values: corridorTxnData.map(d => {
      const total = corridorTxnData.reduce((a, b) => a + b.txns, 0);
      return total > 0 ? Math.round(d.txns / total * 100) : 0;
    }),
    counts: corridorTxnData.map(d => d.txns),
    colors: ['#0EA5E9', '#10B981', '#6366F1', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6']
  };
  const countryBarData = {
    categories: corridorTxnData.map(d => `${d.flag} ${d.label}`),
    series: [{
      name: 'Transactions',
      data: corridorTxnData.map(d => d.txns),
      color: '#0EA5E9'
    }, {
      name: 'Amount (PKR K)',
      data: corridorTxnData.map(d => Math.round(d.amount / 1000)),
      color: '#6366F1'
    }]
  };
  const lineChartData = {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [{
      name: 'Transactions',
      data: [1200, 1900, 1500, 2200, 2800, 3100],
      color: '#4F46E5'
    }, {
      name: 'Amount (PKR K)',
      data: [360, 570, 450, 660, 840, 930],
      color: '#22D3EE'
    }]
  };
  // Shared section card wrapper style
  const sectionCardSx = {
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)',
    border: '1px solid #E8EDF2',
    overflow: 'hidden',
    background: '#FFFFFF'
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-3382" data-mp="6am4iazfc2pa">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-3383" data-mp="3sfr7dts08nzp" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="dashboard" data-id="element-3384" data-mp="3si3wqs03e0ms" />

      <Box component="main" sx={{
      flexGrow: 1,
      p: {
        xs: 2,
        md: 3
      },
      width: {
        sm: `calc(100% - 240px)`
      },
      ml: 0,
      mt: 8,
      position: 'relative'
    }} data-id="element-3385" data-mp="3jnp9a25erjsl">
        {isRefreshing && <LinearProgress sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: 9999,
        background: 'transparent',
        '& .MuiLinearProgress-bar': {
          background: 'linear-gradient(90deg, #22D3EE 0%, #0EA5E9 50%, #4F46E5 100%)'
        }
      }} data-id="element-3386" data-mp="2q2y49kkkeb2b" />}

        <Box sx={{
        width: '100%'
      }} data-id="element-3387" data-mp="390hegrirdzqb">
          {/* Page Header */}
          <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 3
        }} data-id="element-3388" data-mp="34z5xfadyamtq">
            {/* Bank Name - H1, big bold blue, spans ~50% width */}
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }} data-id="element-3389" data-mp="3sprs7zyy2als">
              <Typography variant="h2" sx={{
              fontWeight: 600,
              color: '#64748B',
              fontSize: '1.1rem',
              letterSpacing: '-0.01em',
              lineHeight: 1.4
            }} data-id="element-3390" data-mp="28h8i8rmhwoeu">
                Dashboard — Remittance Operations Overview
              </Typography>

              <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small" sx={{
              bgcolor: '#F1F5F9',
              p: '3px',
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '9px !important',
                px: 2.5,
                py: 0.7,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: '#64748B',
                transition: 'all 200ms ease',
                '&.Mui-selected': {
                  bgcolor: '#FFFFFF',
                  color: '#0F172A',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                  '&:hover': {
                    bgcolor: '#FFFFFF'
                  }
                }
              }
            }} data-id="element-3391" data-mp="1pqhce01pvg57">
                <ToggleButton value="realtime" data-id="element-3392" data-mp="2xfk32fx0faok">
                  <ActivityIcon size={15} style={{
                  marginRight: 6
                }} data-id="element-3393" data-mp="2xe8zoh7h84xz" />
                  Real-time
                </ToggleButton>
                <ToggleButton value="summary" data-id="element-3394" data-mp="2ijcjnhtfedac">
                  <BarChart3Icon size={15} style={{
                  marginRight: 6
                }} data-id="element-3395" data-mp="2ijm8h22qieeg" />
                  Performance
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* ===== REMITTANCE STATISTICS SECTION ===== */}
          <Card sx={{
          ...sectionCardSx,
          mb: 2.5
        }} data-id="element-3396" data-mp="23bhufm8fkuiq">
            <CardContent sx={{
            p: 2.5,
            '&:last-child': {
              pb: 2.5
            }
          }} data-id="element-3397" data-mp="1ycx36cb5pl6f">
              {/* Section Header */}
              <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 2
            }} data-id="element-3398" data-mp="1omln5re77lj6">
                <Typography sx={{
                fontWeight: 700,
                color: '#0F172A',
                fontSize: '1rem',
                letterSpacing: '-0.01em'
              }} data-id="element-3399" data-mp="1anlxcgey90lf">
                  Remittance Statistics
                </Typography>
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }} data-id="element-3400" data-mp="3vb438x3xrkjd">
                  {view === 'summary' && <ToggleButtonGroup value={timeRange} exclusive onChange={handleTimeRangeChange} size="small" sx={{
                  bgcolor: '#F1F5F9',
                  borderRadius: '10px',
                  p: '2px',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '8px !important',
                    px: 2,
                    py: 0.4,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#64748B',
                    '&.Mui-selected': {
                      bgcolor: '#FFFFFF',
                      color: '#0F172A',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      '&:hover': {
                        bgcolor: '#FFFFFF'
                      }
                    }
                  }
                }} data-id="element-3401" data-mp="37kgte6agmku6">
                      <ToggleButton value="mtd" data-id="element-3402" data-mp="3d7vowao72kcf">MTD</ToggleButton>
                      <ToggleButton value="ytd" data-id="element-3403" data-mp="3d6klwd2kpgmd">YTD</ToggleButton>
                    </ToggleButtonGroup>}
                  {view === 'realtime' && <Chip label="● Live — Today" size="small" sx={{
                  bgcolor: '#ECFDF5',
                  color: '#059669',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 28,
                  border: '1px solid #A7F3D0',
                  '& .MuiChip-label': {
                    px: 1
                  }
                }} data-id="element-3404" data-mp="sgmuyadkmtjj" />}
                </Box>
              </Box>

              {/* Filters Row */}
              <Box sx={{
              mb: 2.5,
              p: 1.5,
              bgcolor: '#F8FAFC',
              borderRadius: '12px',
              border: '1px solid #F1F5F9'
            }} data-id="element-3405" data-mp="1oqcfhn6vbejx">
                <Grid container spacing={1.5} alignItems="center" data-id="element-3406" data-mp="22mzk8c52b90e">
                  {view === 'summary' && <>
                      <Grid item xs={12} sm={6} md={2.5} data-id="element-3407" data-mp="27gu177ta99vs">
                        <TextField label="From Date/Time" type="datetime-local" value={fromDate} onChange={e => setFromDate(e.target.value)} fullWidth size="small" InputLabelProps={{
                      shrink: true
                    }} sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#FFFFFF',
                        borderRadius: '10px',
                        fontSize: '0.8rem'
                      }
                    }} data-id="element-3408" data-mp="itzg6ip0k0rw" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.5} data-id="element-3409" data-mp="1l7opmrqgodui">
                        <TextField label="To Date/Time" type="datetime-local" value={toDate} onChange={e => setToDate(e.target.value)} fullWidth size="small" InputLabelProps={{
                      shrink: true
                    }} sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#FFFFFF',
                        borderRadius: '10px',
                        fontSize: '0.8rem'
                      }
                    }} data-id="element-3410" data-mp="1l980g2tclwow" />
                      </Grid>
                    </>}
                  <Grid item xs={12} sm={6} md={view === 'realtime' ? 4 : 2.5} data-id="element-3411" data-mp="2lg4wrhqlky3r">
                    <FormControl fullWidth size="small" data-id="element-3412" data-mp="2qey8p5q8kh1t">
                      <InputLabel data-id="element-3413" data-mp="2qg99cwodx8op">Partner</InputLabel>
                      <Select value={partner} label="Partner" onChange={e => setPartner(e.target.value)} sx={{
                      bgcolor: '#FFFFFF',
                      borderRadius: '10px',
                      fontSize: '0.8rem'
                    }} data-id="element-3414" data-mp="2ufry63jbu3wt">
                        <MenuItem value="all" data-id="element-3415" data-mp="zclf6wfje72u">All Partners</MenuItem>
                        {VALID_MTOS.map(mto => <MenuItem key={mto} value={mto} data-id="element-3416" data-mp="1e0yb5temjpxc">
                            {mto}
                          </MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={view === 'realtime' ? 4 : 2.5} data-id="element-3417" data-mp="1n0d552u02iml">
                    <FormControl fullWidth size="small" data-id="element-3418" data-mp="1j1vtc5zmwjyu">
                      <InputLabel data-id="element-3419" data-mp="3otarcft9ucyk">Country / Corridor</InputLabel>
                      <Select value={corridor} label="Country / Corridor" onChange={e => setCorridor(e.target.value)} sx={{
                      bgcolor: '#FFFFFF',
                      borderRadius: '10px',
                      fontSize: '0.8rem'
                    }} data-id="element-3420" data-mp="3ksh1vi06kq3k">
                        <MenuItem value="all" data-id="element-3421" data-mp="35xkc3ei3a5i5">All Countries</MenuItem>
                        {CORRIDORS.map(c => <MenuItem key={c.code} value={c.code} data-id="element-3422" data-mp="2r09z20pssrkb">
                            {c.flag} {c.label} ({c.code})
                          </MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={view === 'realtime' ? 4 : 2} data-id="element-3423" data-mp="2myogpzuhvl2q">
                    <Button fullWidth variant="contained" disableElevation startIcon={<RefreshCcwIcon size={14} data-id="element-3425" data-mp="l6jun4zofhzx" />} onClick={refreshData} sx={{
                    height: 40,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    bgcolor: '#0F172A',
                    color: '#FFFFFF',
                    '&:hover': {
                      bgcolor: '#1E293B'
                    }
                  }} data-id="element-3424" data-mp="2n0suram3y0gq">
                      Refresh
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* KPI Cards Row */}
              <Box sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'nowrap'
            }} data-id="element-3426" data-mp="115fovixlqqrw">
                <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3427" data-mp="2vgug6469gy9r">
                  <KPICard title="Remittance Received" value={kpiStats.received.value} icon={<DollarSignIcon size={18} data-id="element-3429" data-mp="i62std1ziwqq" />} color="#0EA5E9" trendColorMode="standard" trend={{
                  value: kpiStats.received.trend,
                  label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                  data: kpiStats.received.data
                }} data-id="element-3428" data-mp="d89lggoy3wov" />
                </Box>
                <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3430" data-mp="39tuqettypfw">
                  <KPICard title="Remittance Processed" value={kpiStats.processed.value} icon={<CheckCircle2Icon size={18} data-id="element-3432" data-mp="1erd3emq6jklu" />} color="#10B981" trendColorMode="standard" trend={{
                  value: kpiStats.processed.trend,
                  label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                  data: kpiStats.processed.data
                }} data-id="element-3431" data-mp="89px69mvq8tc" />
                </Box>
                <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3433" data-mp="25xvdhc1xa78e">
                  <KPICard title="Remittance Pending" value={kpiStats.pending.value} icon={<HourglassIcon size={18} data-id="element-3435" data-mp="1i20u7wl9cc38" />} color="#F59E0B" trendColorMode="pending" trend={{
                  value: kpiStats.pending.trend,
                  label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                  data: kpiStats.pending.data
                }} data-id="element-3434" data-mp="1w10gwyp7mgwr" />
                </Box>
                <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3436" data-mp="15onb5cc3pr9e">
                  <KPICard title="Remittance Reversed" value={kpiStats.reversed.value} icon={<RotateCcwIcon size={18} data-id="element-3438" data-mp="1aorsf4ku2q0o" />} color="#3B82F6" trendColorMode="standard" trend={{
                  value: kpiStats.reversed.trend,
                  label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                  data: kpiStats.reversed.data
                }} data-id="element-3437" data-mp="1fh1ickly7q2y" />
                </Box>
                {view === 'realtime' && <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3439" data-mp="41w9szz4yxbf">
                    <KPICard title="Last Txn Date/Time" value={kpiStats.lastTxnDateTime} icon={<CalendarClockIcon size={18} data-id="element-3441" data-mp="ssk3sh8c3k0e" />} color="#6366F1" data-id="element-3440" data-mp="orgqa1dre02c" />
                  </Box>}
                <Box sx={{
                flex: 1,
                minWidth: 0
              }} data-id="element-3442" data-mp="12qq5axq8ao4q">
                  <KPICard title="Avg. Txn Time" value={kpiStats.avgTxnTime} icon={<TimerIcon size={18} data-id="element-3444" data-mp="1ghuwrcjg0a3z" />} color="#10B981" data-id="element-3443" data-mp="17paxc9vbu1i3" />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* ===== Payout Breakdown ===== */}
          <Box sx={{
          mb: 2.5
        }} data-id="element-3445" data-mp="1qgtwu6q56xfd">
            <Typography sx={{
            fontWeight: 600,
            color: '#64748B',
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 1,
            ml: 0.5
          }} data-id="element-3446" data-mp="1vevovh5jq72z">
              Payout Breakdown
            </Typography>
            <Box sx={{
            display: 'flex',
            gap: 1.5
          }} data-id="element-3447" data-mp="3612yhitv0w7p">
              <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3448" data-mp="2i05gcdnxim3">
                <KPICard title="Processed via Cash" value={kpiStats.cash.value} icon={<WalletIcon size={18} data-id="element-3450" data-mp="3ow1hcupdtnc7" />} color="#8B5CF6" trendColorMode="standard" trend={{
                value: kpiStats.cash.trend,
                label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                data: kpiStats.cash.data
              }} data-id="element-3449" data-mp="3tpvvlim7xa3w" />
              </Box>
              <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3451" data-mp="5z4eng4i4697">
                <KPICard title="Processed via FT" value={kpiStats.ft.value} icon={<ArrowRightLeftIcon size={18} data-id="element-3453" data-mp="3s7434tru0iem" />} color="#0EA5E9" trendColorMode="standard" trend={{
                value: kpiStats.ft.trend,
                label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                data: kpiStats.ft.data
              }} data-id="element-3452" data-mp="3n812rmmsr0cz" />
              </Box>
              <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3454" data-mp="expwta629j9g">
                <KPICard title="Processed via IBFT" value={kpiStats.ibft.value} icon={<SendIcon size={18} data-id="element-3456" data-mp="2s5h43o85841k" />} color="#EF4444" trendColorMode="standard" trend={{
                value: kpiStats.ibft.trend,
                label: `vs previous ${timeRange === 'today' ? '24 hours' : timeRange === 'mtd' ? '30 days' : '365 days'}`,
                data: kpiStats.ibft.data
              }} data-id="element-3455" data-mp="17gn37zv5qh9w" />
              </Box>
            </Box>
          </Box>

          {/* ===== VIEW-SPECIFIC CONTENT ===== */}
          {view === 'realtime' ? <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }} data-id="element-3457" data-mp="24awi4vqibd1z">
              {/* Charts */}
              <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            width: '100%'
          }} data-id="element-3458" data-mp="2e93u105xv4x4">
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3459" data-mp="2md3d8qf2wkg4">
                  <ChartCard title="Transaction Status (Today)" type="donut" data={pieChartData} height={320} data-id="element-3460" data-mp="2w8wwesnazc6v" />
                </Box>
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3461" data-mp="23m2adrau84ra">
                  <ChartCard title="Today's Amount Distribution" type="stackedBar" data={barChartData} height={320} data-id="element-3462" data-mp="2clfo4w5zvfht" />
                </Box>
              </Box>

              {/* Country / Corridor Breakdown */}
              <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            width: '100%'
          }} data-id="element-3463" data-mp="1ov362sh1c2di">
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3464" data-mp="18b5mkfm9angu">
                  <ChartCard title="Transactions by Country" type="donut" data={countryChartData} height={320} data-id="element-3465" data-mp="13bsvfiocqu8g" />
                </Box>
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3466" data-mp="tkolwyhg41l0">
                  <ChartCard title="Country Volume Breakdown" type="stackedBar" data={countryBarData} height={320} data-id="element-3467" data-mp="kmuofk7y31w9" />
                </Box>
              </Box>

              {/* Performance Table - Real-time: Today only */}
              <Card sx={sectionCardSx} data-id="element-3468" data-mp="1ykuys98v1590">
                <CardContent sx={{
              p: 0,
              '&:last-child': {
                pb: 0
              }
            }} data-id="element-3469" data-mp="23j65ib3uyzn0">
                  <Box sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #F1F5F9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-3470" data-mp="1ui8bmln4w8i5">
                    <Typography sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#0F172A'
                }} data-id="element-3471" data-mp="16uqcg51ji855">
                      Partner Performance
                    </Typography>
                    <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }} data-id="element-3472" data-mp="11w5dsa1xrwit">
                      <Typography variant="body2" sx={{
                    color: '#94A3B8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem'
                  }} data-id="element-3473" data-mp="s6glimg9ss54">
                        <ClockIcon size={13} data-id="element-3474" data-mp="w7s6geo78p6z" />{' '}
                        {lastRefresh.toLocaleTimeString()}
                      </Typography>
                      <Chip label="Auto-refresh 5m" size="small" sx={{
                    bgcolor: '#ECFDF5',
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 22,
                    border: '1px solid #A7F3D0'
                  }} data-id="element-3475" data-mp="15xboji438f0s" />
                    </Box>
                  </Box>
                  <TableContainer data-id="element-3476" data-mp="1xcg97czfez4d">
                    <Table stickyHeader size="small" data-id="element-3477" data-mp="1xcppg8uo5rwv">
                      <TableHead data-id="element-3478" data-mp="1skenl0gdbw1o">
                        <TableRow data-id="element-3479" data-mp="1sj3imxb6si8z">
                          {['Partner', 'Today Total', 'Today Success', 'Success %', 'Today Amount (PKR)', 'Success Amount (PKR)'].map((header, i) => <TableCell key={header} align={i === 0 ? 'left' : 'right'} sx={{
                        fontWeight: 700,
                        bgcolor: '#F8FAFC',
                        color: '#475569',
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        py: 1.5,
                        borderBottom: '2px solid #E2E8F0'
                      }} data-id="element-3480" data-mp="2bch8wp3yg0i9" data-mpi={i}>
                              {header}
                            </TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-3481" data-mp="1zvt42d1lznzm">
                        {realtimeData.slice(rtPage * rtRowsPerPage, rtPage * rtRowsPerPage + rtRowsPerPage).map((row, idx) => <TableRow key={row.partner} sx={{
                      bgcolor: idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                      '&:hover': {
                        bgcolor: '#F0F9FF'
                      },
                      transition: 'background-color 150ms ease'
                    }} data-id="element-3482" data-mp="33ashjv8in1x" data-mpi={idx}>
                              <TableCell sx={{
                        fontWeight: 600,
                        color: '#0F172A',
                        fontSize: '0.82rem'
                      }} data-id="element-3483" data-mp="3iwlim4799zpv">
                                {row.partner}
                              </TableCell>
                              <TableCell align="right" sx={{
                        fontSize: '0.82rem'
                      }} data-id="element-3484" data-mp="5lwcswz2oeze">
                                {row.todayTotal.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" data-id="element-3485" data-mp="1am42pbc4i0o8">
                                <Typography sx={{
                          color: '#059669',
                          fontWeight: 700,
                          fontSize: '0.82rem'
                        }} data-id="element-3486" data-mp="1aop2lusv26jc">
                                  {row.todaySuccess.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" data-id="element-3487" data-mp="1yd8cgerk6rdj">
                                <Chip label={`${(row.todaySuccess / row.todayTotal * 100).toFixed(1)}%`} size="small" sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: '#ECFDF5',
                          color: '#059669',
                          border: '1px solid #A7F3D0'
                        }} data-id="element-3488" data-mp="23bt25k96psnq" />
                              </TableCell>
                              <TableCell align="right" sx={{
                        fontWeight: 500,
                        fontSize: '0.82rem'
                      }} data-id="element-3489" data-mp="2r28fz3tcyg3i">
                                PKR {row.todayAmount.toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{
                        fontWeight: 500,
                        fontSize: '0.82rem',
                        color: '#059669'
                      }} data-id="element-3490" data-mp="fcflwhbxb90l">
                                PKR{' '}
                                {Math.round(row.todayAmount * 0.95).toLocaleString()}
                              </TableCell>
                            </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination component="div" count={realtimeData.length} page={rtPage} onPageChange={(_, newPage) => setRtPage(newPage)} rowsPerPage={rtRowsPerPage} onRowsPerPageChange={e => {
                setRtRowsPerPage(parseInt(e.target.value, 10));
                setRtPage(0);
              }} rowsPerPageOptions={[5, 10, 25]} sx={{
                borderTop: '1px solid #F1F5F9',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem',
                  color: '#64748B'
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.75rem'
                }
              }} data-id="element-3491" data-mp="16jo6fmiwogvl" />
                </CardContent>
              </Card>

              {/* System Status */}
              <Card sx={{
            borderRadius: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            color: 'white'
          }} data-id="element-3492" data-mp="16h8hhpfrrtlf">
                <CardContent sx={{
              py: 2,
              px: 2.5,
              '&:last-child': {
                pb: 2
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5
            }} data-id="element-3493" data-mp="shpfzfhgrgi3">
                  <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }} data-id="element-3494" data-mp="3i4tyc3egfcns">
                    <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#4ADE80',
                  boxShadow: '0 0 8px #4ADE80, 0 0 16px #4ADE8060'
                }} data-id="element-3495" data-mp="2qefqoox1awkk" />
                    <Typography sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }} data-id="element-3496" data-mp="26pyjagbwb96p">
                      All Systems Operational
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{
                opacity: 0.6,
                fontSize: '0.75rem'
              }} data-id="element-3497" data-mp="1a126eg2m5fpg">
                    API latency: 120ms avg &nbsp;•&nbsp; No active incidents
                    &nbsp;•&nbsp; Last refresh:{' '}
                    {lastRefresh.toLocaleTimeString()}
                  </Typography>
                </CardContent>
              </Card>
            </Box> : <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }} data-id="element-3498" data-mp="1nrx6b2v4wmgw">
              {/* Charts */}
              <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            width: '100%'
          }} data-id="element-3499" data-mp="1xqwe6j82se2u">
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3500" data-mp="27eikbxvpm9h3">
                  <ChartCard title="Transaction Status (Selected Range)" type="donut" data={pieChartData} height={320} data-id="element-3501" data-mp="2cbs6n4nsy50p" />
                </Box>
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3502" data-mp="1onr3ycdb1it7">
                  <ChartCard title="Monthly Trends" type="line" data={lineChartData} height={320} data-id="element-3503" data-mp="1sqdwkfhsv7rg" />
                </Box>
              </Box>

              {/* Country / Corridor Breakdown */}
              <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            width: '100%'
          }} data-id="element-3504" data-mp="1h1sbyltwypas">
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3505" data-mp="1v0s07sdil26f">
                  <ChartCard title="Transactions by Country" type="donut" data={countryChartData} height={320} data-id="element-3506" data-mp="1zxifwelepvrb" />
                </Box>
                <Box sx={{
              flex: 1,
              minWidth: 0
            }} data-id="element-3507" data-mp="2dqrjtzql5p2g">
                  <ChartCard title="Country Volume Breakdown" type="stackedBar" data={countryBarData} height={320} data-id="element-3508" data-mp="2irnncc70z97c" />
                </Box>
              </Box>

              {/* Summary Table - Performance view with MTD/YTD */}
              <Card sx={sectionCardSx} data-id="element-3509" data-mp="2sw43z7sbwuc7">
                <CardContent sx={{
              p: 0,
              '&:last-child': {
                pb: 0
              }
            }} data-id="element-3510" data-mp="2nues5pxa8cn2">
                  <Box sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #F1F5F9'
              }} data-id="element-3511" data-mp="3gjvhuftipffl">
                    <Typography sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#0F172A'
                }} data-id="element-3512" data-mp="36shkoep1grpm">
                      Performance Summary
                    </Typography>
                  </Box>
                  <TableContainer data-id="element-3513" data-mp="3uha9lr78tzaq">
                    <Table stickyHeader size="small" data-id="element-3514" data-mp="1slb4daot6hbn">
                      <TableHead data-id="element-3515" data-mp="1smmh4008ezew">
                        <TableRow data-id="element-3516" data-mp="1xko3atsyfeul">
                          {(() => {
                        const prefix = timeRange === 'ytd' ? 'YTD' : 'MTD';
                        return ['Partner', `${prefix} Total`, `${prefix} Success`, `${prefix} Success %`, 'In Progress', 'Failed', `${prefix} Total (PKR)`, `${prefix} Success (PKR)`];
                      })().map((header, i) => <TableCell key={header} align={i === 0 ? 'left' : 'right'} sx={{
                        fontWeight: 700,
                        bgcolor: '#F8FAFC',
                        color: '#475569',
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        py: 1.5,
                        borderBottom: '2px solid #E2E8F0'
                      }} data-id="element-3517" data-mp="14wig80efhdrl" data-mpi={i}>
                              {header}
                            </TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody data-id="element-3518" data-mp="1gc4z3cy7zjdq">
                        {summaryData.slice(perfPage * perfRowsPerPage, perfPage * perfRowsPerPage + perfRowsPerPage).map((row, idx) => {
                      const actualIdx = perfPage * perfRowsPerPage + idx;
                      const rtRow = realtimeData[actualIdx] || realtimeData[0];
                      return <TableRow key={row.partner} sx={{
                        bgcolor: idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                        '&:hover': {
                          bgcolor: '#F0F9FF'
                        },
                        transition: 'background-color 150ms ease'
                      }} data-id="element-3519" data-mp="3jiarqd8ao12q" data-mpi={idx}>
                                <TableCell sx={{
                          fontWeight: 600,
                          color: '#0F172A',
                          fontSize: '0.82rem'
                        }} data-id="element-3520" data-mp="3ajh0719tm0w1">
                                  {row.partner}
                                </TableCell>
                                <TableCell align="right" sx={{
                          fontSize: '0.82rem'
                        }} data-id="element-3521" data-mp="v6pzgrcr1rl4">
                                  {row.total.toLocaleString()}
                                </TableCell>
                                <TableCell align="right" data-id="element-3522" data-mp="gbab92yuopwn">
                                  <Typography sx={{
                            color: '#059669',
                            fontWeight: 700,
                            fontSize: '0.82rem'
                          }} data-id="element-3523" data-mp="bhedo1mi1520">
                                    {row.success.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" data-id="element-3524" data-mp="3f13s3yqin1u0">
                                  <Chip label={`${row.successPct}%`} size="small" sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: '#ECFDF5',
                            color: '#059669',
                            border: '1px solid #A7F3D0'
                          }} data-id="element-3525" data-mp="3f3q0g0l8dlda" />
                                </TableCell>
                                <TableCell align="right" data-id="element-3526" data-mp="3sztplfh8z3iw">
                                  <Typography sx={{
                            color: '#D97706',
                            fontWeight: 600,
                            fontSize: '0.82rem'
                          }} data-id="element-3527" data-mp="kll56uovpj1t">
                                    {row.inProgress.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" data-id="element-3528" data-mp="2etglvt2nqcuz">
                                  <Typography sx={{
                            color: '#DC2626',
                            fontWeight: 600,
                            fontSize: '0.82rem'
                          }} data-id="element-3529" data-mp="2etyr3f7as1f1">
                                    {row.failed.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{
                          fontWeight: 500,
                          fontSize: '0.82rem'
                        }} data-id="element-3530" data-mp="3cfdy58qg1lyh">
                                  PKR {row.totalAmount.toLocaleString()}
                                </TableCell>
                                <TableCell align="right" sx={{
                          fontWeight: 500,
                          fontSize: '0.82rem',
                          color: '#059669'
                        }} data-id="element-3531" data-mp="1erjvowm6tlan">
                                  PKR {row.successAmount.toLocaleString()}
                                </TableCell>
                              </TableRow>;
                    })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination component="div" count={summaryData.length} page={perfPage} onPageChange={(_, newPage) => setPerfPage(newPage)} rowsPerPage={perfRowsPerPage} onRowsPerPageChange={e => {
                setPerfRowsPerPage(parseInt(e.target.value, 10));
                setPerfPage(0);
              }} rowsPerPageOptions={[5, 10, 25]} sx={{
                borderTop: '1px solid #F1F5F9',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem',
                  color: '#64748B'
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.75rem'
                }
              }} data-id="element-3532" data-mp="85g8a1q6dob5" />
                </CardContent>
              </Card>
            </Box>}
        </Box>
      </Box>
    </Box>;
};
export default Dashboard;